/**
 * Which reading blocks have a voice, and what makes one hearable.
 *
 * A table and a formula are notation — there is nothing worth hearing in them,
 * and a reading control beside a data grid is noise — while a quote, a note and a
 * captioned figure are writing, exactly like a plain reading block. The rule is
 * stated once because it is a property of the *kind*: both studios' editors, both
 * play surfaces and the browser fixtures all ask this question, and a copy of the
 * answer in each is four places to disagree.
 *
 * **A module of its own, and not a section of `#/lib/richBlockKinds`.** That module
 * is where a rich kind's *name and icon* live, which needs the translation macros —
 * and `#/lib/flashbookFixtures` is imported **directly by the Playwright suite**, in
 * Node, where the macro plugin cannot be resolved. So this rule cannot live beside
 * the labels: a fixture that asked its own copy would quietly stop putting a voice
 * on a kind added later, and every audio gate would go on passing while measuring
 * fewer blocks. Types only, here, so the spec-side import stays pure.
 *
 * `offersReadAloud` answers the whole question a play surface has to ask before it
 * draws a transport — the kind, *and* whether the creator's own clip is there. See
 * its own note for why the app has no second voice underneath a passage nobody
 * narrated.
 */
import {type Slide} from '#/types/capsule'

/** The kinds that carry prose a learner can be read aloud. */
export const SPOKEN_KINDS: ReadonlySet<NonNullable<Slide['contentKind']>> =
  new Set(['text', 'quote', 'callout', 'caption'])

/** Whether a reading block of this kind has anything worth hearing. */
export function kindSpeaks(kind: NonNullable<Slide['contentKind']>): boolean {
  return SPOKEN_KINDS.has(kind)
}

/**
 * Whether a block offers a voice to press play on.
 *
 * Two questions, and both have to be yes. The *kind* decides whether hearing the
 * block makes sense at all — a table and a formula are notation, so a reading
 * control beside them is noise whoever made one. The *clip* decides whether there
 * is anything to hear: a read-aloud is the creator's own voice, recorded in the
 * studio or uploaded as a file, and a passage they never narrated is a passage to
 * read. There is no second voice underneath it, and there should not be — the
 * device's speech is not the author, cannot be edited, cannot be levelled against
 * the rest of a book, and reads a formula as characters.
 *
 * So a block with no clip prints no transport, rather than a control that speaks in
 * a stranger's voice.
 */
export function offersReadAloud(block: {
  contentKind?: Slide['contentKind']
  readingAudioUrl?: string | null
}): boolean {
  return kindSpeaks(block.contentKind ?? 'text') && !!block.readingAudioUrl
}
